import { executeManifestQuery } from "./modules.service";

export interface Hierarchy {
  cod_empresa: string;
  canalapp: string;
  is_total: boolean;
  mes_atual: number;
  dif_mes_atual: number;
  variacao_atual: number;
  meta_mes: number;
  meta_mes_anterior: number;
  meta_ano_anterior: number;
  nomeempresaapp: string;
  mes_anterior: number;
  dif_mes_anterior: number;
  variacao_mes_anterior: number;
  ano_anterior: number;
  dif_ano_anterior: number;
  variacao_ano_anterior: number;
  // ponytail: campos exclusivos de flash-de-perdas (perc_*) e flash-de-contribuicao
  // (margem_*) — opcionais porque só um dos dois grupos vem preenchido por módulo
  // (ver app-mobile/src/api/flashPerdas.service.ts e flashContribuicao.service.ts).
  perc_perda?: number;
  perc_meta?: number;
  margem_atual?: number;
  margem_mes_ante?: number;
  margem_ano_ante?: number;
}

interface HierarchyRaw {
  cod_empresa: string | null;
  canalapp: string | null;
  is_total: string;
  mes_atual: string;
  dif_mes_atual: string;
  variacao_atual: string;
  meta_mes: string;
  meta_mes_anterior?: string;
  meta_ano_anterior?: string;
  nomeempresaapp: string | null;
  mes_anterior: string;
  dif_mes_anterior: string;
  variacao_mes_anterior: string;
  ano_anterior: string;
  dif_ano_anterior: string;
  variacao_ano_anterior: string;
  perc_perda?: string;
  perc_meta?: string;
  margem_atual?: string;
  margem_mes_ante?: string;
  margem_ano_ante?: string;
}

const mapHierarchy = (item: HierarchyRaw): Hierarchy => ({
  cod_empresa: item.cod_empresa ?? "",
  canalapp: item.canalapp ?? "",
  is_total: Number(item.is_total) === 1,
  meta_mes: Number(item.meta_mes),
  meta_mes_anterior: Number(item.meta_mes_anterior ?? 0),
  meta_ano_anterior: Number(item.meta_ano_anterior ?? 0),
  nomeempresaapp: item.nomeempresaapp ?? "",
  mes_atual: Number(item.mes_atual),
  dif_mes_atual: Number(item.dif_mes_atual),
  variacao_atual: Number(item.variacao_atual),
  mes_anterior: Number(item.mes_anterior),
  dif_mes_anterior: Number(item.dif_mes_anterior),
  variacao_mes_anterior: Number(item.variacao_mes_anterior),
  ano_anterior: Number(item.ano_anterior),
  dif_ano_anterior: Number(item.dif_ano_anterior),
  variacao_ano_anterior: Number(item.variacao_ano_anterior),
  perc_perda: item.perc_perda !== undefined ? Number(item.perc_perda) : undefined,
  perc_meta: item.perc_meta !== undefined ? Number(item.perc_meta) : undefined,
  margem_atual: item.margem_atual !== undefined ? Number(item.margem_atual) : undefined,
  margem_mes_ante: item.margem_mes_ante !== undefined ? Number(item.margem_mes_ante) : undefined,
  margem_ano_ante: item.margem_ano_ante !== undefined ? Number(item.margem_ano_ante) : undefined,
});

interface HierarchyDiarioRaw {
  cod_empresa: string | null;
  canalapp: string | null;
  is_total: string;
  nomeempresaapp: string | null;
  meta_dia: string;
  dia_atual: string;
  dif_dia_atual: string;
  variacao_atual: string;
  meta_dia_mes_anterior: string;
  dia_mes_anterior: string;
  dif_mes_anterior: string;
  variacao_mes_anterior: string;
  meta_dia_ano_anterior: string;
  dia_ano_anterior: string;
  dif_ano_anterior: string;
  variacao_ano_anterior: string;
}

const mapDiarioToHierarchy = (item: HierarchyDiarioRaw): Hierarchy => ({
  cod_empresa: item.cod_empresa ?? "",
  canalapp: item.canalapp ?? "",
  is_total: Number(item.is_total) === 1,
  meta_mes: Number(item.meta_dia),
  meta_mes_anterior: Number(item.meta_dia_mes_anterior),
  meta_ano_anterior: Number(item.meta_dia_ano_anterior),
  nomeempresaapp: item.nomeempresaapp ?? "",
  mes_atual: Number(item.dia_atual),
  dif_mes_atual: Number(item.dif_dia_atual),
  variacao_atual: Number(item.variacao_atual),
  mes_anterior: Number(item.dia_mes_anterior),
  dif_mes_anterior: Number(item.dif_mes_anterior),
  variacao_mes_anterior: Number(item.variacao_mes_anterior),
  ano_anterior: Number(item.dia_ano_anterior),
  dif_ano_anterior: Number(item.dif_ano_anterior),
  variacao_ano_anterior: Number(item.variacao_ano_anterior),
});

export interface FlashServiceConfig {
  moduleId: string;
  dateParam: string;
  hierarchyQueryId: string;
}

/**
 * Fábrica genérica: os módulos flash-de-vendas/contribuição/perdas têm o mesmo
 * formato de dados e só diferem no module id, no nome do parâmetro de data e no
 * id da query de hierarquia (ver app-mobile/src/api/flash*.service.ts).
 */
export function createFlashService({ moduleId, dateParam, hierarchyQueryId }: FlashServiceConfig) {
  const getHierarchy = (dateYMD: string, clientId: string) =>
    executeManifestQuery<HierarchyRaw[]>(moduleId, hierarchyQueryId, { [dateParam]: dateYMD }, clientId).then(
      (rows) => rows.map(mapHierarchy),
    );

  const getCategorias = (
    dateISO: string,
    codEmpresa: string | null,
    codigoCategoriaPai: string | null,
    clientId: string,
  ) =>
    executeManifestQuery<HierarchyRaw[]>(
      moduleId,
      "flash_categorias",
      { [dateParam]: dateISO, cod_empresa: codEmpresa, codigo_categoria_pai: codigoCategoriaPai },
      clientId,
    ).then((rows) => rows.map(mapHierarchy));

  // ponytail: query ids "flash_diario"/"flash_categorias_diario" são fixos (mesmo nome
  // nos 3 módulos), só o dateParam varia — ver app-mobile/src/api/flashPerdas.service.ts.
  const getHierarchyDiario = (dateISO: string, clientId: string) =>
    executeManifestQuery<HierarchyDiarioRaw[]>(moduleId, "flash_diario", { [dateParam]: dateISO }, clientId).then(
      (rows) => rows.map(mapDiarioToHierarchy),
    );

  const getCategoriasDiario = (
    dateISO: string,
    codEmpresa: string | null,
    codigoCategoriaPai: string | null,
    clientId: string,
  ) =>
    executeManifestQuery<HierarchyDiarioRaw[]>(
      moduleId,
      "flash_categorias_diario",
      { [dateParam]: dateISO, cod_empresa: codEmpresa, codigo_categoria_pai: codigoCategoriaPai },
      clientId,
    ).then((rows) => rows.map(mapDiarioToHierarchy));

  return { getHierarchy, getCategorias, getHierarchyDiario, getCategoriasDiario };
}
